import { Card } from "../../UI/Card.tsx";
import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { Layout } from "../../UI/Layout/Layout.tsx";
import { ScreenTitleBlock } from "../../UI/Layout/ScreenTitleBlock.tsx";
import { BreadCrumbsWrapper } from "../../UI/Layout/BreadCrumbsWrapper.tsx";
import { BreadCrumbsItem } from "../../UI/Layout/BreadCrumbsItem.tsx";
import { ErrorFeedback } from "../../UI/Feedback/ErrorFeedback.tsx";
import { AllUsersScreenPath } from "../AllUserScreen.tsx";
import { useStudentPlanStatistics } from "./useStudentPlanStatistics.ts";
import { useInactiveStudentStatistics } from "./useInactiveStudentStatistics.ts";
import { useUserStatistics } from "./useUserStatistics.ts";
import { newUsersInRecentDays, newUsersQueryRange } from "./newUsersBuckets.ts";
import { UserStatisticsOverview } from "./UserStatisticsOverview.tsx";
import { NewUsersChart } from "./NewUsersChart.tsx";
import { StudentsByLevel } from "./StudentsByLevel.tsx";
import { InactiveStudentsTable } from "./InactiveStudentsTable.tsx";
import { StudentDetailModal } from "./StudentDetailModal.tsx";
import { ExhaustedLessonPackStudentsTable } from "./ExhaustedLessonPackStudentsTable.tsx";

export const UserStatisticsScreenPath = "/users/statistics";

export function UserStatisticsScreen() {
  const navigate = useNavigate();
  const today = useMemo(() => dayjs(), []);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(
    null,
  );
  const [isExhaustedLessonPackListOpen, setIsExhaustedLessonPackListOpen] =
    useState(false);
  const exhaustedLessonPackListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isExhaustedLessonPackListOpen)
      exhaustedLessonPackListRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
  }, [isExhaustedLessonPackListOpen]);

  const { statistics: planStatistics, error: planStatisticsError } =
    useStudentPlanStatistics();
  const { statistics: inactivityStatistics, error: inactivityStatisticsError } =
    useInactiveStudentStatistics();
  const { statistics: userStatistics, error: userStatisticsError } =
    useUserStatistics(newUsersQueryRange(today));
  const statisticsError =
    planStatisticsError ?? inactivityStatisticsError ?? userStatisticsError;

  const recentNewUsers = useMemo(
    () =>
      userStatistics
        ? newUsersInRecentDays({
            newUsersPerDay: userStatistics.newUsersPerDay,
            today,
          })
        : null,
    [userStatistics, today],
  );

  return (
    <Layout>
      {selectedStudentId && (
        <StudentDetailModal
          userId={selectedStudentId}
          onClose={() => setSelectedStudentId(null)}
        />
      )}
      <Card>
        <ScreenTitleBlock
          breadCrumbs={
            <BreadCrumbsWrapper>
              <BreadCrumbsItem
                onClick={() => navigate(AllUsersScreenPath)}
                title={"Users"}
              />
              <BreadCrumbsItem title={"Statistics"} />
            </BreadCrumbsWrapper>
          }
          title={"Statistics"}
        />
      </Card>
      <div className={"mt-4 flex flex-col gap-4"}>
        {statisticsError && (
          <ErrorFeedback
            title={"Some statistics could not be loaded"}
            message={statisticsError.message}
          />
        )}
        <UserStatisticsOverview
          planStatistics={planStatistics}
          userStatistics={userStatistics}
          newUsersInRecentDays={recentNewUsers}
          isExhaustedLessonPackListOpen={isExhaustedLessonPackListOpen}
          onToggleExhaustedLessonPackList={() =>
            setIsExhaustedLessonPackListOpen((isOpen) => !isOpen)
          }
        />
        {isExhaustedLessonPackListOpen && planStatistics && (
          <ExhaustedLessonPackStudentsTable
            ref={exhaustedLessonPackListRef}
            students={planStatistics.studentsWithExhaustedLessonPack}
            today={today}
            onSelectStudent={setSelectedStudentId}
            onClose={() => setIsExhaustedLessonPackListOpen(false)}
          />
        )}
        <div className={"grid grid-cols-1 lg:grid-cols-2 gap-4"}>
          <NewUsersChart
            newUsersPerDay={userStatistics?.newUsersPerDay ?? null}
            today={today}
          />
          <StudentsByLevel
            levelCounts={planStatistics?.activePlanStudentsByLevel ?? null}
          />
        </div>
        <InactiveStudentsTable
          inactiveStudents={inactivityStatistics?.inactiveStudents ?? null}
          inactivityWindowDays={
            inactivityStatistics?.inactivityWindowDays ?? null
          }
          today={today}
          onSelectStudent={setSelectedStudentId}
        />
      </div>
    </Layout>
  );
}
